import { useEffect, useRef, useSyncExternalStore } from "react";
import { clearScreenDraft, draftSaveError, loadScreenDraft } from "./screenDraft";

/**
 * 하단 고정 저장바 — 화면마다 흩어져 있던 저장/게시 버튼을 **한 자리**로 모은다.
 *
 * 왜: 버튼이 화면마다 위/아래 제각각이었고 고정돼 있지도 않아, 목록이 길어지면 화면 밖으로
 * 사라졌다. 게다가 한 화면에 패널이 여럿이면(행사·랠리 = 행사+선물+지점) 저장 버튼이 3개였다.
 * 패널이 "나 저장할 게 있어요"를 등록하면, 앱이 **하나의 바**로 모아 그린다.
 *
 * dirty 를 주면 바뀐 개수가 표시되고 0이면 비활성. 안 주면 항상 누를 수 있다
 * (목록형 패널처럼 '무엇이 바뀌었는지' 세기 어려운 경우).
 */
export interface SaveReg {
  id: string;
  label: string; // 무엇을 올리는지 (여러 개일 때 구분용)
  dirty?: number;
  save: () => Promise<void>;
  /**
   * 💾 **웹에 올리지 않고 하던 일만 저장.** 있는 화면에서만 [저장하기] 버튼이 뜬다.
   *
   * 전자책처럼 한 번에 못 끝내는 작업 때문에 생겼다 — 저장하는 길이 [웹에 올리기] 하나뿐이라
   * 중간 저장이 곧 **미완성 게시**였다(2026-08-21 요청).
   */
  saveDraft?: () => Promise<void>;
  /**
   * 💾 **화면이 넘겨주는 「지금 하던 일」.** 이것만 주면 [저장하기] 버튼·불러오기 안내가
   * 자동으로 붙는다 — 화면마다 localStorage 를 다시 짜지 않게(20개 화면이 있다).
   *
   * `data()` 는 저장 시점에 부른다(렌더마다 새 객체를 만들어도 상관없게).
   * `restore(d)` 는 운영자가 [불러오기]를 눌렀을 때 그 값을 화면에 되돌려 놓는다.
   */
  draft?: { data: () => unknown; restore: (d: unknown) => void };
}

// ── store(리액트 밖) — 훅 없이도 테스트할 수 있게 순수 함수로 분리 ──
const regs = new Map<
  string,
  {
    label: string;
    dirty?: number;
    save: () => Promise<void>;
    saveDraft?: () => Promise<void>;
    restore?: (d: unknown) => void;
  }
>();
const subs = new Set<() => void>();
let snapshot: SaveReg[] = [];

function rebuild(): void {
  snapshot = [...regs].map(([id, r]) => ({ id, ...r }));
  subs.forEach((f) => f());
}

/** 등록(또는 갱신) 후 해제 함수를 돌려준다. 화면에 보이는 값이 바뀌었으니 구독자에게 알린다. */
export function registerSave(reg: SaveReg): () => void {
  // `draft` 를 준 화면은 저장·불러오기를 여기서 만들어 준다(화면은 값만 넘긴다).
  const fromDraft = reg.draft;
  const saveDraft = reg.saveDraft
    ? reg.saveDraft
    : fromDraft
      ? async () => {
          // 실패를 조용히 넘기지 않는다 — **왜** 안 됐는지까지 저장바가 적을 수 있게 던진다.
          const why = draftSaveError(reg.id, fromDraft.data());
          if (why) throw new Error(why);
        }
      : undefined;
  regs.set(reg.id, {
    label: reg.label,
    dirty: reg.dirty,
    save: reg.save,
    saveDraft,
    restore: fromDraft?.restore,
  });
  rebuild();
  return () => {
    regs.delete(reg.id);
    rebuild();
  };
}

/** 저장 동작만 갈아끼운다 — 화면에 보이는 건 그대로라 **알리지 않는다**(불필요한 렌더 방지). */
export function setSaveFn(id: string, save: () => Promise<void>): void {
  const cur = regs.get(id);
  if (cur) cur.save = save;
}

export function getSaveRegs(): SaveReg[] {
  return snapshot;
}

/**
 * 저장 안 한 게 있나 — **한 곳에서** 판정한다.
 *
 * 예전엔 `beforeunload` 경고가 전자책·체험공간 배치 **두 화면에만** 있었다. 굿즈·캐릭터·
 * AR 카드·직접 만들기는 실수로 창을 닫으면 그냥 날아갔다. 화면마다 붙이면 새 화면을 만들 때
 * 또 빠뜨린다 — 저장바에 등록하는 순간 자동으로 지켜지게 여기 둔다.
 *
 * `dirty` 를 안 주는 패널은 셀 수가 없다(목록형이라 '무엇이 바뀌었는지' 세기 어려운 경우).
 * 그건 여기서 0으로 본다 — **모르는 것을 있다고 우기면** 아무것도 안 바꿨는데 경고가 떠서,
 * 운영자는 곧 경고 자체를 무시하게 된다.
 */
export function unsavedCount(): number {
  return snapshot.reduce((n, r) => n + (r.dirty ?? 0), 0);
}

/**
 * 이 화면에 **불러올 수 있는 보관본**이 있나. 저장바가 안내를 띄울지 정한다.
 * 값 자체는 안 돌려준다 — 불러오는 건 restoreDraft 한 곳에서만 한다.
 */
export function pendingDrafts(): { id: string; label: string; at: number }[] {
  const out: { id: string; label: string; at: number }[] = [];
  for (const [id, r] of regs) {
    if (!r.restore) continue;
    const d = loadScreenDraft(id);
    if (d) out.push({ id, label: r.label, at: d.at });
  }
  return out;
}

/** 보관본을 화면에 되돌린다. 되돌린 뒤에도 **보관본은 지우지 않는다** — 실수로 눌렀을 수 있다. */
export function restoreDraft(id: string): boolean {
  const r = regs.get(id);
  const d = loadScreenDraft(id);
  if (!r?.restore || !d) return false;
  r.restore(d.data);
  return true;
}

/** 보관본 버리기 — 운영자가 명시적으로 누를 때만. */
export function discardDraft(id: string): void {
  clearScreenDraft(id);
  rebuild(); // 안내가 사라져야 한다
}

/** 테스트용 초기화. */
export function resetSaveRegs(): void {
  regs.clear();
  rebuild();
}

// ── 리액트 바인딩 ──

/** 화면(패널)이 자기 저장 동작을 등록. 언마운트되면 사라진다. */
export function useRegisterSave(reg: SaveReg): void {
  // save 는 렌더마다 새 함수라 의존성에 넣으면 무한 재등록이 된다 — 최신 것만 갈아끼우고,
  // 화면에 보이는 값(label·dirty)이 바뀔 때만 재등록해 구독자에게 알린다.
  const saveRef = useRef(reg.save);
  saveRef.current = reg.save;
  setSaveFn(reg.id, () => saveRef.current());

  const draftRef = useRef(reg.saveDraft);
  draftRef.current = reg.saveDraft;
  // `draft` 를 준 화면도 [저장하기]가 생긴다 — 둘 중 하나만 있으면 된다.
  const dataRef = useRef(reg.draft?.data);
  dataRef.current = reg.draft?.data;
  const restoreRef = useRef(reg.draft?.restore);
  restoreRef.current = reg.draft?.restore;
  /**
   * 🔴 **둘을 갈라 봐야 한다.**
   *
   * 예전엔 `hasDraft`(= `saveDraft` 또는 `draft`) 하나로 아래 `saveDraft` 를 만들었다.
   * 그런데 화면들은 전부 `draft` 만 넘긴다(17개 전부). 그러면 `draftRef.current` 는
   * **undefined 인데 껍데기 함수는 생겨서**, `registerSave` 가 「저장 함수를 직접 받았구나」로
   * 보고 `draft` 로 만들 기회를 잃는다. 누르면 `undefined()` — 저장바는 그걸 삼켜
   * 「저장 못 함」 한 줄만 남겼다. 화면·타입·린트 어디에도 이상이 없었다.
   */
  const hasOwnSaveDraft = !!reg.saveDraft;
  const hasDraft = hasOwnSaveDraft || !!reg.draft;

  useEffect(
    () =>
      registerSave({
        id: reg.id,
        label: reg.label,
        dirty: reg.dirty,
        save: () => saveRef.current(),
        // 있는지 없는지만 바뀌면 다시 등록한다(버튼이 생기고 사라지는 건 화면에 보이는 변화다)
        // 화면이 **직접 준 것이 있을 때만** 넘긴다 — 없으면 아래층이 `draft` 로 만들게 둔다.
        saveDraft: hasOwnSaveDraft ? () => draftRef.current!() : undefined,
        draft: dataRef.current
          ? { data: () => dataRef.current!(), restore: (d) => restoreRef.current?.(d) }
          : undefined,
      }),
    [reg.id, reg.label, reg.dirty, hasDraft, hasOwnSaveDraft],
  );
}

function subscribe(f: () => void): () => void {
  subs.add(f);
  return () => subs.delete(f);
}

/** 앱 셸이 읽어 하단 바를 그린다. */
export function useSaveRegs(): SaveReg[] {
  return useSyncExternalStore(subscribe, getSaveRegs, getSaveRegs);
}
